// Quellenverweise stehen im Markdown als reiner Text - "[1]", "[2, 3]", "[4-6]".
// Dieses rehype-Plugin hebt sie beim Rendern hoch, damit sie klein und hochgestellt
// im Lesefluss stehen, ohne dass die Autoren im Editor HTML schreiben müssen.
import type { Element, ElementContent, Root } from 'hast'

// Nur reine Zahlenlisten - so bleiben Markdown-Reste wie "[siehe oben]" unangetastet.
const CITATION_PATTERN = /\[\d+(?:\s*[,;–-]\s*\d+)*\]/g

// In diesen Elementen ist eine eckige Klammer Teil des Inhalts, kein Verweis.
const SKIP_TAGS = new Set([
  'code',
  'pre',
  'a',
  'sup',
  'math',
  'annotation',
  'semantics',
  'script',
  'style',
])

const CITATION_CLASS = [
  'text-[0.7em]',
  'align-super',
  'leading-none',
  'font-normal',
  'text-foreground/55',
  'dark:text-white/55',
]

// KaTeX rendert seine Formeln als span-Bäume voller Klammern - die bleiben tabu.
function isKatex(node: Element): boolean {
  const className = node.properties?.className
  return (
    Array.isArray(className) &&
    className.some((c) => typeof c === 'string' && c.startsWith('katex'))
  )
}

function citationNode(value: string): Element {
  return {
    type: 'element',
    tagName: 'sup',
    properties: { className: CITATION_CLASS },
    children: [{ type: 'text', value }],
  }
}

function transform(parent: Root | Element): void {
  const next: ElementContent[] = []
  let changed = false

  for (const child of parent.children as ElementContent[]) {
    if (child.type === 'element') {
      if (!SKIP_TAGS.has(child.tagName) && !isKatex(child)) transform(child)
      next.push(child)
      continue
    }

    if (child.type !== 'text') {
      next.push(child)
      continue
    }

    const matches = Array.from(child.value.matchAll(CITATION_PATTERN))
    if (matches.length === 0) {
      next.push(child)
      continue
    }

    changed = true
    let cursor = 0
    for (const match of matches) {
      const start = match.index ?? 0
      if (start > cursor) next.push({ type: 'text', value: child.value.slice(cursor, start) })
      next.push(citationNode(match[0]))
      cursor = start + match[0].length
    }
    if (cursor < child.value.length) next.push({ type: 'text', value: child.value.slice(cursor) })
  }

  if (changed) parent.children = next
}

export function rehypeCitations() {
  return (tree: Root) => {
    transform(tree)
  }
}
